import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiContext } from '@shared/contexts';
import { TUseFileResult, TFile, TFileVersion } from './types';
import { TVersionInfo, useRefetchItemStore } from '@modules/items';

// --- GET:

export const useFile = (id: string) => {
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);
  const refetchEnabled = useRefetchItemStore((state) => state.refetchEnabled);

  const getFile = async (signal?: AbortSignal): Promise<TUseFileResult> => {
    const res = await apiClient.get(`/files/${id}`, { signal });
    res.data.file.versions.reverse();
    return res.data;
  };

  const { data, isError, error } = useQuery({
    retry,
    queryKey: [`file-${id}`],
    queryFn: ({ signal }) => getFile(signal),
    refetchOnWindowFocus: refetchEnabled,
    refetchOnMount: refetchEnabled,
    refetchOnReconnect: refetchEnabled,
  });
  useHandleQueryError(isError, error, `Failed to load file ${id}!`, 'Loading file failed!');

  return { error, data };
};

export const useCreateVersion = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: {
    id: string;
    jsFile: File;
    note: string;
    versionId: string;
  }): Promise<TFileVersion> => {
    const formData = new FormData();
    formData.append('file', variables.jsFile, variables.jsFile.name);
    formData.append('note', variables.note);
    formData.append('version', variables.versionId);

    const res = await apiClient.post(`/files/${variables.id}/version`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to create new version!', true),
    onSuccess: (data, variables) => {
      // file query cache
      queryClient.setQueryData(
        [`file-${variables.id}`],
        (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
          oldData && {
            ...oldData,
            file: {
              ...oldData.file,
              versions: [data, ...oldData.file.versions],
              stats: { ...oldData.file.stats, latestVersionId: variables.versionId },
            },
          }
      );
      // version-info query cache
      queryClient.setQueryData(
        [`version-info-${variables.id}`],
        (oldData: TVersionInfo | undefined): TVersionInfo | undefined =>
          oldData
            ? {
                ...oldData,
                versions: [data, ...(oldData.versions as TFileVersion[])],
                // no need to update `approvals` here, as they are not updated in the backend
              }
            : oldData
      );
      show('success', 'New version uploaded.');
    },
  });
};

// --- POST:
export const useSignFileVersion = () => {
  const { apiClient, queryClient, handleApiError, show } = useContext(ApiContext);

  const mutationFn = async (variables: { id: string; versionId: string }): Promise<TFileVersion> => {
    const res = await apiClient.post(`/files/${variables.id}/sign?version=${variables.versionId}`);
    return res.data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to sign version!', true),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        [`file-${variables.id}`],
        (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
          oldData && {
            ...oldData,
            file: {
              ...oldData.file,
              versions: oldData.file.versions.map((v) => (v.id === variables.versionId ? data : v)),
            },
          }
      );
      show('success', 'Version signed.');

      return data;
    },
  });
};

export const useUpdateLastExternallyApprovedFile = () => {
  const { apiClient, handleApiError, show } = useContext(ApiContext);
  const queryClient = useQueryClient();

  const mutationFn = async (variables: { id: string; file?: File | null }): Promise<TFile> => {
    const formData = new FormData();
    if (variables.file) formData.append('file', variables.file);
    const { data } = await apiClient.put(`/files/${variables.id}/last-externally-approved`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  };

  return useMutation({
    mutationFn,
    onError: handleApiError('Failed to update externally approved file!', true),
    onSuccess: (file, variables) => {
      show('success', variables.file ? 'Uploaded approved PDF.' : 'Removed approved PDF.');
      queryClient.setQueryData(
        [`file-${variables.id}`],
        (oldData: TUseFileResult | undefined): TUseFileResult | undefined =>
          oldData
            ? {
                ...oldData,
                file: {
                  ...oldData.file,
                  lastExternallyApproved: file.lastExternallyApproved,
                  stats: { ...oldData.file.stats, modifiedDate: file.stats.modifiedDate },
                },
              }
            : oldData
      );
    },
  });
};
